import { IconButton, Menu, MenuItem } from '@mui/material';
import { Print, ArrowDropDown } from '@mui/icons-material';
import { useAlert } from "components/UI/alertSakeBar/alertProvider";
import Loading from "components/Loading";
import { useFullScreenTabDetect, useMobileDetect } from "containers/chat/hooks";
import React, { useRef, useState } from "react";
import ReactDOM from "react-dom/client";
import { useLocation, useNavigate } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import { get } from "lodash";
import styled from "styled-components";
import AnimatedCheckMark from "components/AnimatedCheckMark";
import AnimatedCross from "components/AnimatedCross";
import "./printVocab.css";
import { WordDocumentComponent, WordDocumentCognatesComponent } from "./VocabWordDocument";
import { ExcelDocumentComponent, ExcelDocumentCognatesComponent } from "./VocabExcel";

/* ====== Styled Components ====== */
const StyledTh = styled.th`
    background: #5d4187;
    color: #ffffff;
    padding: 8px;
    border: 0.5px solid black;
    border-left: 0.5px solid black;
    border-right: 0.5px solid black;
    border-top: 0.5px solid black;
    border-bottom: 0.5px solid black;
    text-align: left;
    
    @media print {
        border: 0.5px solid black !important;
        border-left: 0.5px solid black !important;
        border-right: 0.5px solid black !important;
        border-top: 0.5px solid black !important;
        border-bottom: 0.5px solid black !important;
        background: #5d4187 !important;
        color: #ffffff !important;
        -webkit-print-color-adjust: exact !important;
        color-adjust: exact !important;
        print-color-adjust: exact !important;
        padding: 4px !important;
        font-size: 13px !important;
    }
`;

const PrintTh = styled.th`
  background: #5d4187;
  color: #ffffff;
  padding: 8px;
  text-align: left;

  @media print {
    background: #5d4187 !important;
    color: #ffffff !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
    padding: 4px !important;
    font-size: 13px !important;
  }
`;


const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;

  td, th {
    padding: 8px;
    word-wrap: break-word;
    white-space: normal;
  }

  @media print {
    width: 100% !important;
    max-width: 100% !important;
    border-collapse: collapse !important;
    table-layout: fixed !important;

    td, th {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      padding: 4px !important;
      font-size: 13px !important;
      word-wrap: break-word !important;
      overflow-wrap: break-word !important;
      min-width: auto !important;
      max-width: none !important;
    }

    thead th {
      background: #5d4187 !important;
      color: #ffffff !important;
    }

    thead {
      display: table-header-group;
    }
  }
`;



const StyledHeader = styled.tr`
    background-color: #fff;
    // position: sticky;
    // top: 0;
    // z-index: 10;
    // border: none !important;
`;

const StyledTableRow = styled.tr`
    /* Ensure rows stay together during printing */
    page-break-inside: avoid;
    break-inside: avoid;
    
    @media print {
        page-break-inside: avoid !important;
        break-inside: avoid !important;
    }
`;

const PrintStyles = styled.div`
    @media print {
        body, html {
            width: 100%;
            height: auto;
            overflow: visible !important;
        }

        .print-container {
            width: 100% !important;
            max-width: 100% !important;
            height: auto !important;
            overflow: visible !important;
            position: relative;
            padding: 0 !important;
            margin: 0 !important;
        }

        .printable {
            width: 100% !important;
            max-width: 100% !important;
            height: auto;
        }

        table {
            width: 100% !important;
            max-width: 100% !important;
            table-layout: fixed !important;
            page-break-inside: auto;
            border-collapse: collapse !important;
            border-spacing: 0 !important;
        }
        
        tr {
            page-break-inside: avoid;
            break-inside: avoid;
            page-break-after: auto;
        }
        
        td, th {
            page-break-inside: avoid;
            break-inside: avoid;
            padding: 4px !important;
            font-size: 13px !important;
            word-wrap: break-word !important;
            overflow-wrap: break-word !important;
        }

        thead {
            display: table-header-group;
        }

        tfoot {
            display: table-footer-group;
        }
    }
`;



/* ====== Print Header Component ====== */
const PrintHeaderCognates = ({ vocType }) => {
    return (
        <StyledHeader>
            <StyledTh style={{ minWidth: 150, maxWidth: 250 }}>Cognate</StyledTh>
            <StyledTh style={{ minWidth: 150 }}>Word (Spanish)</StyledTh>
            <StyledTh>Word (English)</StyledTh>
            <StyledTh style={{ minWidth: 200 }}>Type</StyledTh>
            <StyledTh style={{ minWidth: 200 }}>Explanation</StyledTh>
            <StyledTh style={{ minWidth: 200 }}>Sentence in Spanish</StyledTh>
            <StyledTh style={{ minWidth: 200 }}>Sentence in English</StyledTh>
        </StyledHeader>
    );
};

/* ====== Print Component ====== */
const PrintComponentCognates = React.forwardRef(({ getBooksVocab, vocType }, ref) => {
    return (
        <PrintStyles>
            <div ref={ref} className="print-container printable">
                <style>{`
                    @page { 
                        size: letter landscape; 
                        margin: 10mm; 
                    }
                    
                    @media print {
                        * {
                            box-sizing: border-box !important;
                        }
                        
                        .print-container {
                            width: 100% !important;
                            max-width: 100% !important;
                            padding: 0 !important;
                            margin: 0 !important;
                        }
                        
                        table {
                            width: 100% !important;
                            max-width: 100% !important;
                            table-layout: fixed !important;
                            page-break-inside: auto;
                        }
                        
                        tr {
                            page-break-inside: avoid !important;
                            break-inside: avoid !important;
                        }
                        
                        td, th {
                            page-break-inside: avoid !important;
                            break-inside: avoid !important;
                            padding: 4px !important;
                            font-size: 13px !important;
                            word-wrap: break-word !important;
                            overflow-wrap: break-word !important;
                            min-width: auto !important;
                            max-width: none !important;
                        }
                        
                        tbody tr {
                            page-break-inside: avoid !important;
                            break-inside: avoid !important;
                        }
                    }
                `}</style>
                <div style={{ width: "100%", maxWidth: "100%", padding: 10 }}>
                    <StyledTable>
                        <thead>
                            <PrintHeaderCognates vocType={vocType} />
                        </thead>
                        <tbody>
                            {getBooksVocab.map((value, index) => {
                                let data = get(value, 'data', '');
                                return (
                                  <StyledTableRow key={index}>
                                    <td style={{ border: '1px solid black' }}>
                                      <span>
                                        {data?.type ? (
                                          <AnimatedCheckMark
                                            animation={false}
                                          />
                                        ) : (
                                          <AnimatedCross animation={false} />
                                        )}
                                      </span>
                                    </td>
                                    <td style={{ border: '1px solid black' }}>
                                      <span>
                                        {get(data, "word_spanish") || "-"}
                                      </span>
                                    </td>
                                    <td style={{ border: '1px solid black' }}>
                                      <span>
                                        {get(data, "word_english") || "-"}
                                      </span>
                                    </td>
                                    <td style={{ border: '1px solid black' }}>
                                      <span>
                                        {data?.type
                                          ? "True Cognate"
                                          : "False Cognate" || "-"}
                                      </span>
                                    </td>
                                    <td style={{ border: '1px solid black' }}>
                                      <span>
                                        {get(data, "explaination") || "-"}
                                      </span>
                                    </td>
                                    <td style={{ border: '1px solid black' }}>
                                      <span>
                                        {get(data, "sentence_spanish") || "-"}
                                      </span>
                                    </td>
                                    <td style={{ border: '1px solid black' }}>
                                      <span>
                                        {get(data, "sentence_english") || "-"}
                                      </span>
                                    </td>
                                  </StyledTableRow>
                                );
                            })}
                        </tbody>
                    </StyledTable>
                </div>
            </div>
        </PrintStyles>
    );
});

/* ====== Print Header Component for Phrases ====== */
const PrintHeaderPhrases = ({ vocType }) => {
    return (
        <StyledHeader>
            <StyledTh style={{ minWidth: 150, maxWidth: 250 }}>Phrase</StyledTh>
            <StyledTh style={{ minWidth: 150 }}>Translation</StyledTh>
            <StyledTh style={{ minWidth: 200 }}>Sentence in Spanish</StyledTh>
            <StyledTh style={{ minWidth: 200 }}>Sentence in English</StyledTh>
        </StyledHeader>
    );
};

/* ====== Print Header Component ====== */
const PrintHeader = ({ vocType, bookLanguage }) => {
    return (
        // <StyledHeader>
        //     <PrintTh style={{ minWidth: 150, maxWidth: 250 }}>Word</PrintTh>
        //     <PrintTh style={{ minWidth: 150 }}>Translation</PrintTh>
        //     <PrintTh>Definition</PrintTh>
        //     {vocType === 'nonSpanish' ? (
        //         <>
        //             <PrintTh style={{ minWidth: 200 }}>Synonyms in Spanish</PrintTh>
        //             <PrintTh style={{ minWidth: 200 }}>Synonyms in English</PrintTh>
        //             <PrintTh style={{ minWidth: 200 }}>Antonyms in Spanish</PrintTh>
        //             <PrintTh style={{ minWidth: 200 }}>Antonyms in English</PrintTh>
        //             <PrintTh style={{ minWidth: 200 }}>Sentence in Spanish</PrintTh>
        //             <PrintTh style={{ minWidth: 200 }}>Sentence in English</PrintTh>
        //         </>
        //     ) : (
        //         <>
        //             <PrintTh style={{ minWidth: 200 }}>Synonyms in Spanish</PrintTh>
        //             <PrintTh style={{ minWidth: 200 }}>Synonyms in English</PrintTh>
        //             <PrintTh style={{ minWidth: 200 }}>Antonyms in Spanish</PrintTh>
        //             <PrintTh style={{ minWidth: 200 }}>Antonyms in English</PrintTh>
        //             <PrintTh style={{ minWidth: 200 }}>Sentence in Spanish</PrintTh>
        //             <PrintTh style={{ minWidth: 200 }}>Sentence in English</PrintTh>
        //         </>
        //     )}
        // </StyledHeader>

        <StyledHeader>
            <PrintTh style={{ minWidth: 150, maxWidth: 250 }}>Word</PrintTh>
            <PrintTh style={{ minWidth: 150 }}>Translation</PrintTh>
            <PrintTh>Definition</PrintTh>
            {vocType ? (
                <>
                    <StyledTh style={{ minWidth: 200 }}>Synonyms in {bookLanguage === 'french' ? 'French' : 'Spanish'}</StyledTh>
                    <StyledTh style={{ minWidth: 200 }}>Synonyms in English</StyledTh>
                    <StyledTh style={{ minWidth: 200 }}>Antonyms in {bookLanguage === 'french' ? 'French' : 'Spanish'}</StyledTh>
                    <StyledTh style={{ minWidth: 200 }}>Antonyms in English</StyledTh>
                    <StyledTh style={{ minWidth: 200 }}>Sentence in {bookLanguage === 'french' ? 'French' : 'Spanish'}</StyledTh>
                    <StyledTh style={{ minWidth: 200 }}>Sentence in English</StyledTh>
                </>
            ) : (
                <>
                    <PrintTh style={{ minWidth: 200 }}>Synonyms</PrintTh>
                    <PrintTh style={{ minWidth: 200 }}>Antonyms</PrintTh>
                    <PrintTh style={{ minWidth: 200 }}>Sentence</PrintTh>
                </>
            )}
        </StyledHeader>
    );
};

/* ====== Print Component ====== */
const PrintComponent = React.forwardRef(({ getBooksVocab, vocType, bookLanguage }, ref) => {
    return (
        <PrintStyles>
            <div ref={ref} className="print-container printable">
                <style>{`
                    @page { 
                        size: letter landscape; 
                        margin: 10mm; 
                    }
                    
                    @media print {
                        * {
                            box-sizing: border-box !important;
                        }
                        
                        .print-container {
                            width: 100% !important;
                            max-width: 100% !important;
                            padding: 0 !important;
                            margin: 0 !important;
                        }
                        
                        table {
                            width: 100% !important;
                            max-width: 100% !important;
                            table-layout: fixed !important;
                            page-break-inside: auto;
                        }
                        
                        tr {
                            page-break-inside: avoid !important;
                            break-inside: avoid !important;
                        }
                        
                        td, th {
                            page-break-inside: avoid !important;
                            break-inside: avoid !important;
                            padding: 4px !important;
                            font-size: 13px !important;
                            word-wrap: break-word !important;
                            overflow-wrap: break-word !important;
                            min-width: auto !important;
                            max-width: none !important;
                        }
                        
                        tbody tr {
                            page-break-inside: avoid !important;
                            break-inside: avoid !important;
                        }
                    }
                `}</style>
                <div style={{ width: "100%", maxWidth: "100%", padding: 10 }}>
                    <StyledTable>
                        <thead>
                            <PrintHeader vocType={vocType} bookLanguage={bookLanguage} />
                        </thead>
                        <tbody>
                            {getBooksVocab.map((value, index) => {
                                let data = get(value, 'data', '');
                                // console.log('Print data:', data);
                                return (
                                    <StyledTableRow key={index}>
                                        <td><span>{get(data, 'word') || '-'}</span></td>
                                        <td><span>{get(data, 'english_translation') || '-'}</span></td>
                                        <td><span>{get(data, 'definition') || '-'}</span></td>
                                        {vocType ? (
                                            <>
                                                <td><span>{get(data, `synonyms_in_${bookLanguage}`) || '-'}</span></td>
                                                <td><span>{get(data, 'synonyms_in_english') || '-'}</span></td>
                                                <td><span>{get(data, `antonyms_in_${bookLanguage}`) || '-'}</span></td>
                                                <td><span>{get(data, 'antonyms_in_english') || '-'}</span></td>
                                                <td><span>{get(data, `sentence_in_${bookLanguage}`) || '-'}</span></td>
                                                <td><span>{get(data, 'sentence_in_english') || '-'}</span></td>
                                            </>
                                        ) : (
                                            // <>
                                            //         <td style={{ padding: 5, border: '1px solid black' }}>
                                            //             <span>
                                            //                 {get(data, 'synonyms_in_spanish') || '-'}
                                            //             </span>
                                            //         </td>
                                            //         <td style={{ padding: 5, border: '1px solid black' }}>
                                            //             <span>
                                            //                 {get(data, 'synonyms_in_english') || '-'}
                                            //             </span>
                                            //         </td>
                                            //         <td style={{ padding: 5, border: '1px solid black' }}>
                                            //             <span>
                                            //                 {get(data, 'antonyms_in_spanish') || '-'}
                                            //             </span>
                                            //         </td>
                                            //         <td style={{ padding: 5, border: '1px solid black' }}>
                                            //             <span>
                                            //                 {get(data, 'antonyms_in_english') || '-'}
                                            //             </span>
                                            //         </td>
                                            //     <td style={{padding: 5, border: '1px solid black'}}><span>{get(data, 'sentence_in_spanish') || '-'}</span></td>
                                            //     <td style={{padding: 5, border: '1px solid black'}}><span>{get(data, 'sentence_in_english') || '-'}</span></td>
                                            // </>
                                            <>
                                                    <td style={{ padding: 5 }}>
                                                        <span>
                                                            {Array.isArray(get(data, 'synonyms'))
                                                                ? get(data, 'synonyms').join(', ')
                                                                : '-'}
                                                        </span>
                                                    </td>
                                                    <td style={{ padding: 5 }}>
                                                        <span>
                                                            {Array.isArray(get(data, 'antonyms'))
                                                                ? get(data, 'antonyms').join(', ')
                                                                : '-'}
                                                        </span>
                                                    </td>
                                                <td style={{padding: 5}}><span>{get(data, 'sentence') || '-'}</span></td>
                                            </>
                                        )}
                                    </StyledTableRow>
                                );
                            })}
                        </tbody>
                    </StyledTable>
                </div>
            </div>
        </PrintStyles>
    );
});

/* ====== Print Component for Phrases ====== */
const PrintComponentPhrases = React.forwardRef(({ getBooksVocab, vocType }, ref) => {
    return (
        <PrintStyles>
            <div ref={ref} className="print-container printable">
                <style>{`
                    @page { 
                        size: letter landscape; 
                        margin: 10mm; 
                    }
                    
                    @media print {
                        * {
                            box-sizing: border-box !important;
                        }
                        
                        .print-container {
                            width: 100% !important;
                            max-width: 100% !important;
                            padding: 0 !important;
                            margin: 0 !important;
                        }
                        
                        table {
                            width: 100% !important;
                            max-width: 100% !important;
                            table-layout: fixed !important;
                            page-break-inside: auto;
                        }
                        
                        tr {
                            page-break-inside: avoid !important;
                            break-inside: avoid !important;
                        }
                        
                        td, th {
                            page-break-inside: avoid !important;
                            break-inside: avoid !important;
                            padding: 4px !important;
                            font-size: 13px !important;
                            word-wrap: break-word !important;
                            overflow-wrap: break-word !important;
                            min-width: auto !important;
                            max-width: none !important;
                        }
                        
                        tbody tr {
                            page-break-inside: avoid !important;
                            break-inside: avoid !important;
                        }
                    }
                `}</style>
                <div style={{ width: "100%", maxWidth: "100%", padding: 10 }}>
                    <StyledTable>
                        <thead>
                            <PrintHeaderPhrases vocType={vocType} />
                        </thead>
                        <tbody>
                            {getBooksVocab.map((value, index) => {
                                let data = get(value, 'data', '');
                                return (
                                    <tr key={index}>
                                        <td><span>{get(data, 'phrase') || '-'}</span></td>
                                        <td><span>{get(data, 'english_translation') || '-'}</span></td>
                                        <td><span>{get(data, 'sentence') || '-'}</span></td>
                                        <td><span>{get(data, 'sentence_english') || '-'}</span></td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </StyledTable>
                </div>
            </div>
        </PrintStyles>
    );
});

/* ====== Main Print Function ====== */
const VocabPrint = ({ data, vocType, toggleType, bookLanguage }) => {
    const componentRef = useRef();
    const [printLoader, setPrintLoader] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const showAlert = useAlert();
    const location = useLocation();
  	const navigate = useNavigate();
    const isTablet = useFullScreenTabDetect();
    const isMobile = useMobileDetect() && !isTablet;
    const handlePrint = useReactToPrint({
        contentRef: componentRef,
        documentTitle: "Custom Print Document",
        pageStyle: "@page { size: letter landscape; margin: 10mm; }",
        onBeforeGetContent: async () => setPrintLoader(true),
        onBeforePrint: async () => setPrintLoader(false),
    });

    const redirectToNextPage = () => {
        navigate({
            pathname: "/print_assignment",
            state: data,
        });
    };

    const isChromeOniOS = () => {
        const userAgent = navigator.userAgent || navigator.vendor || window.opera;
        return /iPad|iPhone|iPod/.test(userAgent) && /CriOS/.test(userAgent);
    };

    const isMacOS = () => {
        const userAgent = navigator.userAgent || navigator.vendor || window.opera;
        return /Macintosh|MacIntel|MacPPC|Mac68K|iPhone|iPad|iPod/.test(userAgent);
    };

    const isWindowsOrAndroid = () => {
        const userAgent = navigator.userAgent || navigator.vendor || window.opera;
        return /Windows|Android/.test(userAgent);
    };

    // Dropdown functions
    const handleDropdownOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleDropdownClose = () => {
        setAnchorEl(null);
    };

    const handlePrintOption = () => {
        handleDropdownClose();
        onPrint();
    };

    const handleWordDocumentOption = () => {
        handleDropdownClose();
        // if (isMobile) return showAlert("Word document generation is not supported on this device!", "error");
        // if (isTablet) return showAlert("Word document generation is not supported on this device!", "error");
        const validData = data.filter(item => item?.data?.word || item?.data?.word_spanish || item?.data?.word_english || item?.data?.phrase);
        if (validData.length === 0) return showAlert("No Vocabulary/Cognate to print!", "error");
        
        setPrintLoader(true);
        
        try {
            const tempContainer = document.createElement('div');
            tempContainer.style.position = 'absolute';
            tempContainer.style.left = '-9999px';
            tempContainer.style.top = '-9999px';
            tempContainer.style.visibility = 'hidden';
            document.body.appendChild(tempContainer);
            
            const validData = data.filter(item => item?.data?.word || item?.data?.word_spanish || item?.data?.word_english || item?.data?.phrase);
            const wordComponent = toggleType === 'congantes' || toggleType === 'unit_cognates' || toggleType === 'my_student_cog' 
                ? React.createElement(WordDocumentCognatesComponent, { getBooksVocab: validData, vocType: vocType })
                : React.createElement(WordDocumentComponent, { getBooksVocab: validData, vocType: vocType });
            
            const root = ReactDOM.createRoot(tempContainer);
            root.render(wordComponent);
            
            setTimeout(() => {
                try {
                    const downloadButton = tempContainer.querySelector('button');
                    if (downloadButton) {
                        downloadButton.click();
                        showAlert("Word document generated successfully!", "success");
                    } else {
                        showAlert("Error: Download button not found!", "error");
                    }
                } catch (error) {
                    showAlert("Error generating Word document!", "error");
                } finally {
                    setTimeout(() => {
                        try {
                            root.unmount();
                            if (document.body.contains(tempContainer)) {
                                document.body.removeChild(tempContainer);
                            }
                        } catch (cleanupError) {
                            console.error('Cleanup error:', cleanupError);
                        }
                        setPrintLoader(false);
                    }, 1000);
                }
            }, 500);
            
        } catch (error) {
            console.error('Error generating Word document:', error);
            showAlert("Error generating Word document!", "error");
            setPrintLoader(false);
        }
    };

    const handleExcelOption = () => {
        handleDropdownClose();
        // if (isMobile) return showAlert("Excel generation is not supported on this device!", "error");
        // if (isTablet) return showAlert("Excel generation is not supported on this device!", "error");
        const validData = data.filter(item => item?.data?.word || item?.data?.word_spanish || item?.data?.word_english || item?.data?.phrase);
        if (validData.length === 0) return showAlert("No Vocabulary/Cognate to print!", "error");
        
        setPrintLoader(true);
        
        try {
            const tempContainer = document.createElement('div');
            tempContainer.style.position = 'absolute';
            tempContainer.style.left = '-9999px';
            tempContainer.style.top = '-9999px';
            tempContainer.style.visibility = 'hidden';
            document.body.appendChild(tempContainer);
            
            const validData = data.filter(item => item?.data?.word || item?.data?.word_spanish || item?.data?.word_english || item?.data?.phrase);
            const excelComponent = toggleType === 'congantes' || toggleType === 'unit_cognates' || toggleType === 'my_student_cog' 
                ? React.createElement(ExcelDocumentCognatesComponent, { getBooksVocab: validData, vocType: vocType })
                : React.createElement(ExcelDocumentComponent, { getBooksVocab: validData, vocType: vocType });
            
            const root = ReactDOM.createRoot(tempContainer);
            root.render(excelComponent);
            
            setTimeout(() => {
                try {
                    const downloadButton = tempContainer.querySelector('button');
                    if (downloadButton) {
                        downloadButton.click();
                        showAlert("Excel file generated successfully!", "success");
                    } else {
                        showAlert("Error: Download button not found!", "error");
                    }
                } catch (error) {
                    showAlert("Error generating Excel file!", "error");
                } finally {
                    setTimeout(() => {
                        try {
                            root.unmount();
                            if (document.body.contains(tempContainer)) {
                                document.body.removeChild(tempContainer);
                            }
                        } catch (cleanupError) {
                            console.error('Cleanup error:', cleanupError);
                        }
                        setPrintLoader(false);
                    }, 1000);
                }
            }, 500);
            
        } catch (error) {
            console.error('Error generating Excel file:', error);
            showAlert("Error generating Excel file!", "error");
            setPrintLoader(false);
        }
    };

    const onPrint = () => {
        if (isMobile) return showAlert("Print is not supported on this device!", "error");
        if (isTablet) return showAlert("Print is not supported on this device!", "error");
        const validData = data.filter(item => item?.data?.word || item?.data?.word_spanish || item?.data?.word_english || item?.data?.phrase);
        if (validData.length === 0) return showAlert("No Vocabulary/Cognate to print!", "error");
        isChromeOniOS() ? redirectToNextPage() : handlePrint();
    };

    return (
         <div className="w-fit-content">
            <IconButton 
                style={{ padding: 0, color: "white" }} 
                aria-label="print" 
                onClick={handleDropdownOpen}
            >
                {printLoader ? <Loading size={20} /> : <Print style={{ color: "black" }} />}
                <ArrowDropDown style={{ color: "black", fontSize: 16 }} />
            </IconButton>
            
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleDropdownClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                style={{ zIndex: 1000000002 }}
            >
                <MenuItem onClick={handlePrintOption}>
                    Print
                </MenuItem>
                {!isMacOS() && (
                    <MenuItem onClick={handleWordDocumentOption}>
                        Download Word Document
                    </MenuItem>
                )}
                {!isWindowsOrAndroid() && (
                    <MenuItem onClick={handleExcelOption}>
                        Download Excel
                    </MenuItem>
                )}
            </Menu>
            <div style={{ display: "none" }} className="printable">
                {toggleType === 'congantes' || toggleType === 'unit_cognates' || toggleType === 'my_student_cog' ? (
                    <PrintComponentCognates ref={componentRef} getBooksVocab={data} vocType={vocType} />
                ) : toggleType === 'page_phrases' || toggleType === 'my_phrases_stud' || toggleType === 'unit_phrases' ? (
                    <PrintComponentPhrases ref={componentRef} getBooksVocab={data} vocType={vocType} />
                ) : (
                    <PrintComponent ref={componentRef} getBooksVocab={data} vocType={vocType} bookLanguage={bookLanguage} />
                )}
            </div>
        </div>
    );
};

export default VocabPrint;
